import { ConsoleContinue } from "components/platform/companyConsole/ConsoleState";
import { JournalPosting as Posting } from "components/common/transactions/TransactionJournal";
import React, { useEffect, useRef, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { Alert, Box, Button, Card, Divider, Typography } from "@mui/material";
import { Chip, DataTable, Drawer, ScrollArea, toast } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import {
  COMPANY_JOURNAL,
  COMPANY_JOURNAL_ENTRY,
  COMPANY_RECONCILIATION,
  COMPANY_DISCREPANCIES,
} from "graphql/companyConsole";
import {
  useCompanyConnection,
  type CompanyConnection,
} from "hooks/useCompanyConnection";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { completeReport, downloadReport } from "./reportExport";
import { ConsoleError } from "./ConsoleState";
import {
  ConsoleProps,
  JournalEntry,
  instant,
  label,
  money,
  dateFilter,
} from "./types";

interface Reconciliation {
  currency?: string;
  coverage: string;
  source?: string;
  issuerStatus: string;
  transactionStatus: string;
  issuerCheckedAt?: number;
  transactionCheckedAt?: number;
  issuerThrough?: number;
  transactionThrough?: number;
  generation?: number;
  asOf?: number;
  balances?: string;
}
interface Discrepancy {
  id: string;
  boundary: string;
  finding: string;
  status: string;
  subject: string;
  detail: string;
  posting?: string;
  updatedAt?: number;
}

export function CompanyLedger({ companyId, params, navigate }: ConsoleProps) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const financialAccountId = params.get("ledgerAccount");
  const filter = {
    ...dateFilter(params),
    currency: params.get("currency") || undefined,
  };
  const page = useCompanyConnection<JournalEntry>({
    query: COMPANY_JOURNAL,
    variables: {
      companyId,
      financialAccountId,
      filter,
      transactionId: params.get("journalTransaction") || undefined,
    },
    field: "companyJournal",
    skip: !financialAccountId,
  });
  const discrepancies = useCompanyConnection<Discrepancy>({
    query: COMPANY_DISCREPANCIES,
    variables: { companyId, financialAccountId },
    field: "companyDiscrepancies",
    skip: !financialAccountId,
  });
  const checked = useQuery<{ companyReconciliation: Reconciliation }>(
    COMPANY_RECONCILIATION,
    {
      variables: { companyId, financialAccountId },
      skip: !financialAccountId,
      fetchPolicy: "no-cache",
    },
  );
  const reconciliation = checked.data?.companyReconciliation;
  const sequence = Number(params.get("entry"));
  const detail = useQuery<{ companyJournalEntry: JournalEntry }>(
    COMPANY_JOURNAL_ENTRY,
    {
      variables: { companyId, financialAccountId, sequence },
      skip: !financialAccountId || !params.has("entry"),
      fetchPolicy: "no-cache",
    },
  );
  const selected = detail.data?.companyJournalEntry;
  const setSelected = (entry: JournalEntry | null) =>
    navigate({ entry: entry ? String(entry.sequence) : null });
  const [finding, setFinding] = useState<Discrepancy | null>(null);
  const [exporting, setExporting] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const exportJournal = async () => {
    if (exporting || !financialAccountId) return;
    setExporting(true);
    try {
      const csv = await completeReport<JournalEntry>(
        [
          "entry_id",
          "financial_account_id",
          "sequence",
          "entry_type",
          "effective_at",
          "recorded_at",
          "currency",
          "ledger_account",
          "debit_minor",
          "credit_minor",
          "transaction_id",
          "financial_event_id",
          "authorization_id",
          "through_sequence",
        ],
        async (after) => {
          const result = await client.query<{
            companyJournal: CompanyConnection<JournalEntry>;
          }>({
            query: COMPANY_JOURNAL,
            variables: {
              transactionId: params.get("journalTransaction") || undefined,
              companyId,
              financialAccountId,
              filter,
              first: 100,
              after,
            },
            fetchPolicy: "no-cache",
          });
          if (result.data.companyJournal.coverage !== "COVERED")
            throw new Error("Journal unavailable");
          return result.data.companyJournal;
        },
        (node, data) =>
          node.lines.map((line) => [
            node.id,
            node.financialAccountId,
            node.sequence,
            node.type,
            new Date(node.effectiveAt).toISOString(),
            new Date(node.recordedAt).toISOString(),
            line.currency,
            line.ledgerAccount,
            line.debit ?? "",
            line.credit ?? "",
            node.transactionId ?? "",
            node.references?.financialEventId ?? "",
            node.references?.authorizationId ?? "",
            data.throughSequence,
          ]),
        () => !mounted.current,
      );
      downloadReport(csv, `journal-${companyId}-${financialAccountId}.csv`);
    } catch {
      if (mounted.current)
        toast.error(t("The journal export could not be completed"));
    } finally {
      if (mounted.current) setExporting(false);
    }
  };
  const exportFindings = async () => {
    if (exporting || !financialAccountId || !reconciliation) return;
    setExporting(true);
    const observedAt = new Date().toISOString();
    try {
      const csv = await completeReport<Discrepancy>(
        [
          "company_id",
          "financial_account_id",
          "finding_id",
          "boundary",
          "finding",
          "status",
          "subject",
          "updated_at",
          "detail",
          "posting",
          "observed_at",
          "journal_sequence",
        ],
        async (after) =>
          (
            await client.query<{
              companyDiscrepancies: CompanyConnection<Discrepancy>;
            }>({
              query: COMPANY_DISCREPANCIES,
              variables: { companyId, financialAccountId, first: 100, after },
              fetchPolicy: "no-cache",
            })
          ).data.companyDiscrepancies,
        (row) => [
          [
            companyId,
            financialAccountId,
            row.id,
            row.boundary,
            row.finding,
            row.status,
            row.subject,
            row.updatedAt ? new Date(row.updatedAt).toISOString() : "",
            row.detail,
            row.posting ?? "",
            observedAt,
            reconciliation.generation,
          ],
        ],
        () => !mounted.current,
      );
      downloadReport(
        csv,
        `reconciliation-${companyId}-${financialAccountId}.csv`,
      );
    } catch {
      if (mounted.current)
        toast.error(t("The reconciliation export could not be completed"));
    } finally {
      if (mounted.current) setExporting(false);
    }
  };
  const columns: DataColumn<JournalEntry>[] = [
    { id: "sequence", header: t("Sequence"), cell: (row) => row.sequence },
    { id: "type", header: t("Entry"), cell: (row) => label(row.type) },
    {
      id: "amount",
      header: t("Amount"),
      align: "right",
      cell: (row) => money(row.amount, row.currency),
    },
    {
      id: "lines",
      header: t("Posting lines"),
      cell: (row) => row.lines.length,
    },
    {
      id: "effective",
      header: t("Effective"),
      cell: (row) => instant(row.effectiveAt),
    },
    {
      id: "recorded",
      header: t("Recorded"),
      cell: (row) => instant(row.recordedAt),
    },
  ];
  if (!financialAccountId)
    return (
      <Alert severity="info">
        {t("Choose a financial account to view its ledger and reconciliation")}
      </Alert>
    );
  return (
    <Stack gap="var(--space-4)" sx={{ flex: 1, minHeight: 0 }}>
      {(page.error || checked.error || discrepancies.error) && <ConsoleError />}
      <Inline justify="between" wrap>
        <Stack gap="var(--space-2)">
          <Typography variant="subtitle2">{t("Reconciliation")}</Typography>
          <Inline gap="var(--space-3)" wrap>
            <Chip tone="neutral">
              {t("Issuer to journal")}: {label(reconciliation?.issuerStatus)}
            </Chip>
            <Chip tone="neutral">
              {t("Journal to transactions")}:{" "}
              {label(reconciliation?.transactionStatus)}
            </Chip>
          </Inline>
          <Typography variant="caption">
            {t("Last checked")}: {instant(reconciliation?.issuerCheckedAt)} ·{" "}
            {t("Transactions")}: {instant(reconciliation?.transactionCheckedAt)}
          </Typography>
          {reconciliation?.generation != null && (
            <Typography variant="caption">
              {t("Journal sequence")}: {reconciliation.generation} ·{" "}
              {t("Issuer checked through")}:{" "}
              {instant(reconciliation.issuerThrough)} ·{" "}
              {t("Transactions through")}:{" "}
              {reconciliation.transactionThrough ?? 0}
            </Typography>
          )}
        </Stack>
        <Button
          variant="default-outlined"
          onClick={() => void exportJournal()}
          disabled={exporting || page.connection?.coverage !== "COVERED"}
        >
          {t(exporting ? "Exporting all entries" : "Export all entries")}
        </Button>
      </Inline>
      {page.connection?.coverage === "NOT_COVERED" && (
        <Alert severity="warning">
          {t(
            "This account does not have a certified journal, Reconciliation coverage is unavailable",
          )}
        </Alert>
      )}
      {reconciliation?.balances && (
        <Stack gap="var(--space-2)">
          <Typography variant="subtitle2">
            {t("Stored journal balances")} · {reconciliation.source} ·{" "}
            {instant(reconciliation.asOf)}
          </Typography>
          <Inline gap="var(--space-3)" wrap>
            {Object.entries(
              JSON.parse(reconciliation.balances) as Record<string, number>,
            ).map(([bucket, amount]) => (
              <Chip key={bucket} tone="neutral">
                {label(bucket)}: {money(amount, reconciliation.currency)}
              </Chip>
            ))}
          </Inline>
        </Stack>
      )}
      <ConsoleContinue paused={page.paused} loadMore={page.loadMore} />
      <Box ref={page.viewportRef} sx={{ flex: 1, minHeight: 180 }}>
        <DataTable
          columns={columns}
          rows={page.rows}
          getRowKey={(row) => row.id}
          loading={page.loading}
          isFetchingMore={page.isFetchingMore}
          onScroll={page.handleScroll}
          onRowClick={setSelected}
          stickyHeader
          emptyState={t("No journal entries")}
        />
      </Box>
      <ConsoleContinue
        paused={discrepancies.paused}
        loadMore={discrepancies.loadMore}
      />
      <Card variant="outlined" sx={{ p: "var(--space-3)" }}>
        <Inline justify="between">
          <Typography variant="subtitle2">
            {t("Stored reconciliation findings")}
          </Typography>
          <Button
            variant="default-outlined"
            disabled={exporting || !reconciliation || discrepancies.loading}
            onClick={() => void exportFindings()}
          >
            {t("Export findings")}
          </Button>
        </Inline>
        <Box
          ref={discrepancies.viewportRef}
          onScroll={discrepancies.handleScroll}
          sx={{ maxHeight: 160, overflow: "auto" }}
        >
          {discrepancies.rows.map((row) => (
            <Button
              key={row.id}
              variant="default-soft"
              fullWidth
              onClick={() => setFinding(row)}
            >
              {label(row.boundary)} · {label(row.finding)} · {label(row.status)}
            </Button>
          ))}
          {!discrepancies.loading && !discrepancies.rows.length && (
            <Typography variant="body2">
              {t("No stored findings in the available coverage")}
            </Typography>
          )}
          {(discrepancies.loading || discrepancies.isFetchingMore) && (
            <Typography role="status">{t("Loading findings")}</Typography>
          )}
        </Box>
      </Card>
      <Drawer
        loading={detail.loading}
        open={params.has("entry")}
        onClose={() => setSelected(null)}
        title={t("Journal entry")}
      >
        {detail.error && <ConsoleError />}
        {selected && (
          <Stack gap="var(--space-5)">
            <Posting entry={selected} />
            {selected.transactionId && (
              <Button
                variant="default-outlined"
                onClick={() => {
                  navigate({
                    transaction: selected.transactionId!,
                    entry: null,
                  });
                }}
              >
                {t("View transaction")}
              </Button>
            )}
            {selected.transactionId && (
              <Button
                variant="default-outlined"
                onClick={() =>
                  navigate({
                    tab: "activity",
                    activity: "events",
                    resource: selected.transactionId!,
                    entry: null,
                  })
                }
              >
                {t("Related activity")}
              </Button>
            )}
          </Stack>
        )}
      </Drawer>
      <Drawer
        open={!!finding}
        onClose={() => setFinding(null)}
        title={t("Reconciliation finding")}
      >
        {finding && (
          <Stack gap="var(--space-4)">
            <Typography>
              {label(finding.boundary)} · {label(finding.finding)}
            </Typography>
            <Typography>{finding.subject}</Typography>
            <Typography variant="body2">
              {instant(finding.updatedAt)}
            </Typography>
            <ScrollArea>
              <Box
                component="pre"
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {JSON.stringify(JSON.parse(finding.detail), null, 2)}
              </Box>
            </ScrollArea>
            {finding.posting && (
              <Box component="pre" sx={{ whiteSpace: "pre-wrap" }}>
                {JSON.stringify(JSON.parse(finding.posting), null, 2)}
              </Box>
            )}
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
